import regAside from '@/assets/images/reg-aside.jpg';

const wregPhotoModules = import.meta.glob<{ default: string }>('@/assets/images/wreg/*.jpg', {
  eager: true,
});

const photoByName: Record<string, string> = Object.fromEntries(
  Object.entries(wregPhotoModules).map(([path, mod]) => [path.split('/').pop() as string, mod.default]),
);

const photo = (name: string): string | undefined => photoByName[name];

export const WREG_ORDER = [
  'intake',
  'intake-contact',
  'intake-about',
  'intake-mbi',
  'screener',
  'tracks',
  'agreement',
  'switch',
  'labs',
  'monitor',
  'allset',
  'x-notelig',
  'x-already',
  'x-control',
  'x-mismatch',
  'x-noqualify',
  'x-error',
  'x-switchok',
  'x-outreach',
  'x-labspending',
  'x-declined',
] as const;

export type WregStep = (typeof WREG_ORDER)[number];

export type WregGroup = 'intake' | 'care' | 'labs' | 'app' | 'exception';

export const WREG_TOTAL_STEPS = 8;

export const WREG_WORKING_MS = 1500;

export const WREG_GROUP: Partial<Record<WregStep, WregGroup>> = {
  intake: 'intake',
  'intake-contact': 'intake',
  'intake-about': 'intake',
  'intake-mbi': 'intake',
  screener: 'care',
  tracks: 'care',
  agreement: 'care',
  switch: 'care',
  labs: 'labs',
  monitor: 'app',
  allset: 'app',
};

export const wregGroup = (step: WregStep): WregGroup => WREG_GROUP[step] ?? 'exception';

export const WREG_ASIDE: Record<WregGroup, string> = {
  intake: 'wreg.aside.intake',
  care: 'wreg.aside.care',
  labs: 'wreg.aside.labs',
  app: 'wreg.aside.app',
  exception: 'wreg.aside.exception',
};

export const PHOTO_FILE: Partial<Record<WregStep, string>> = {
  screener: 'wreg-step-2.jpg',
  tracks: 'wreg-step-2.jpg',
  agreement: 'wreg-step-3.jpg',
  switch: 'wreg-step-3.jpg',
  labs: 'wreg-step-4.jpg',
  monitor: 'wreg-aside-monitor.jpg',
  allset: 'wreg-step-6.jpg',
};

export const INTAKE_STEPS = WREG_ORDER.filter((step) => WREG_GROUP[step] === 'intake');

export const WREG_PHOTOS: Partial<Record<WregStep, string>> = {
  ...Object.fromEntries(INTAKE_STEPS.map((step) => [step, regAside])),
  ...Object.fromEntries(
    (Object.keys(PHOTO_FILE) as WregStep[])
      .map((step) => [step, photo(PHOTO_FILE[step] as string)])
      .filter(([, url]) => typeof url === 'string'),
  ),
};

export const WREG_PHOTO_LIST: string[] = [
  ...new Set(Object.values(WREG_PHOTOS) as string[]),
];

export const wregPhoto = (step: WregStep): string => WREG_PHOTOS[step] ?? regAside;

export type WregNumberedStep = Exclude<WregStep, `x-${string}`>;

const WREG_STEP_NUMBER: Record<WregNumberedStep, number> = {
  intake: 1,
  'intake-contact': 2,
  'intake-about': 3,
  'intake-mbi': 4,
  screener: 5,
  tracks: 5,
  agreement: 5,
  switch: 5,
  labs: 6,
  monitor: 7,
  allset: 8,
};

const isNumberedStep = (step: WregStep): step is WregNumberedStep => step in WREG_STEP_NUMBER;

export const wregStepNumber = (step: WregStep): number | null =>
  isNumberedStep(step) ? WREG_STEP_NUMBER[step] : null;

export const WREG_STEP_NAME: Record<WregStep, string> = {
  intake: 'details_form',
  'intake-contact': 'contact_info_form',
  'intake-about': 'about_you_form',
  'intake-mbi': 'medicare_number_form',
  screener: 'condition_screening',
  tracks: 'programs',
  agreement: 'terms',
  switch: 'switch_provider',
  labs: 'lab_results',
  monitor: 'shipping_details',
  allset: 'download_app',
  'x-notelig': 'not_eligible',
  'x-already': 'already_enrolled',
  'x-control': 'control_group',
  'x-mismatch': 'confirm_details',
  'x-noqualify': 'no_qualifying_condition',
  'x-error': 'error',
  'x-switchok': 'switch_complete',
  'x-outreach': 'labs_outreach',
  'x-labspending': 'labs_pending',
  'x-declined': 'declined',
};

export const WELCOME_STEP_NAME = 'welcome';
